import React from 'react'
import { AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react'
import Modal from '../Modal/Modal'
import Button from '../Button/Button'
import './ConfirmDialog.css'

/**
 * ConfirmDialog Component
 *
 * A reusable confirmation dialog for destructive or important actions
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Dialog visibility state
 * @param {Function} props.onConfirm - Confirm action handler
 * @param {Function} props.onCancel - Cancel action handler
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message
 * @param {string} props.confirmText - Confirm button text (default: "Confirm")
 * @param {string} props.cancelText - Cancel button text (default: "Cancel")
 * @param {string} props.variant - Dialog variant (danger, warning, info, success)
 * @param {boolean} props.loading - Loading state for confirm button
 */
const ConfirmDialog = ({
	isOpen,
	onConfirm,
	onCancel,
	title = 'Confirm Action',
	message = 'Are you sure you want to proceed?',
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	variant = 'warning',
	loading = false,
}) => {
	/**
	 * Get icon based on variant
	 */
	const getIcon = () => {
		const iconProps = { size: 48, className: `confirm-dialog__icon--${variant}` }

		switch (variant) {
			case 'danger':
				return <XCircle {...iconProps} />
			case 'warning':
				return <AlertTriangle {...iconProps} />
			case 'success':
				return <CheckCircle {...iconProps} />
			case 'info':
			default:
				return <Info {...iconProps} />
		}
	}

	/**
	 * Get confirm button variant
	 */
	const getConfirmButtonVariant = () => {
		switch (variant) {
			case 'danger':
			case 'warning':
				return 'outlined'
			default:
				return 'primary'
		}
	}

	const footer = (
		<>
			<Button variant="secondary" onClick={onCancel} disabled={loading}>
				{cancelText}
			</Button>
			<Button
				variant={getConfirmButtonVariant()}
				onClick={onConfirm}
				loading={loading}
				className={variant === 'danger' || variant === 'warning' ? 'btn-danger' : ''}
			>
				{confirmText}
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			size="small"
			footer={footer}
			closeOnEscape={!loading}
			closeOnBackdrop={!loading}
			showCloseButton={false}
		>
			<div className="confirm-dialog">
				<div className="confirm-dialog__icon-wrapper">{getIcon()}</div>
				<h3 className="confirm-dialog__title">{title}</h3>
				<p className="confirm-dialog__message">{message}</p>
			</div>
		</Modal>
	)
}

export default ConfirmDialog
